/**
 * O critério de "estes dois toques são o MESMO contato, repetido" — a metade que falta para o
 * duplo toque existir no quadro branco.
 *
 * ─── por que este arquivo existe separado ───────────────────────────────────────────────────
 *
 * Duas razões, e a segunda é a que decide.
 *
 * 1. `pointer.ts` é arquivo do upstream, e tudo que se escreve lá vira patch a manter a cada
 *    atualização do Affine (Regra 8.15 — *arquivo novo não conflita; arquivo editado
 *    conflita*). O que sobra lá é a fiação; a decisão mora aqui.
 *
 *    ⚠️ E `pointer.ts` é do `framework/std`, que até o card 1864882251313972786 **nenhum patch
 *    nosso tocava**. É a primeira vez que se mexe no framework — razão a mais para a edição ser
 *    de uma expressão só.
 *
 * 2. **Este arquivo não importa NADA.** Sem `@blocksuite`, sem tipo do DOM, sem zod. É isso que
 *    permite `cli/tests/js/duplo-toque.test.ts` executá-lo de verdade nesta estação, sem
 *    `node_modules` do Affine e sem os ~16 minutos de build que a Regra 8.7.3 cataloga — o
 *    mesmo mecanismo que `decidir-pan.ts` comprou no card 1864291811674032036.
 *
 *    ⚠️ No dia em que precisar importar de `@blocksuite`, ele deixou de ser puro e o teste para
 *    de carregar. Isso é o gate, não um acidente.
 *
 * ─── o defeito que ele corrige ──────────────────────────────────────────────────────────────
 *
 * O duplo clique do editor **não vem do `dblclick` do navegador**. O `ClickController` o
 * sintetiza contando `pointerdown`, e a contagem original exige que os dois toques tenham o
 * **mesmo `pointerId`**:
 *
 * ```
 * event.pointerId === this._downPointerState.raw.pointerId
 * ```
 *
 * Isso é verdadeiro no mouse e **falso por construção no dedo**: a spec de Pointer Events dá um
 * `pointerId` novo a cada contato. Medido em 2026-09-15, com controle no mesmo minuto:
 *
 * | entrada | `pointerId` dos dois toques | iguais? |
 * |---|---|---|
 * | dedo    | `[2, 3]` | ❌ não |
 * | mouse   | `[1, 1]` | ✅ sim |
 *
 * Com a identidade falhando, o contador volta a 1 a cada toque, `_pointerDownCount === 2` nunca
 * é verdade, e `doubleClick` **nunca é emitido**. Nada falha: o gesto simplesmente não existe.
 *
 * ─── por que o critério do dedo não pode ser o do mouse ─────────────────────────────────────
 *
 * No mouse, a identidade do ponteiro já responde "é o mesmo dedo?" de graça, e a proximidade é
 * garantida pelo hardware — o cursor não se teletransporta entre dois cliques. No dedo não há
 * identidade E não há garantia de proximidade: dois dedos diferentes podem tocar em pontos
 * distantes dentro da mesma janela de tempo. **A identidade é substituída por proximidade**, e é
 * por isso que `FOLGA_DO_TOQUE` não é um detalhe de tolerância: ele é o que impede dois toques
 * em lugares diferentes de virarem um duplo toque.
 */

/** Os três valores que um `PointerEvent.pointerType` assume — mais o que navegador nenhum promete. */
export type TipoDePonteiro = 'mouse' | 'pen' | 'touch' | (string & {});

/**
 * A folga em pixels que o framework usa para decidir que um ponteiro "andou".
 *
 * ⚠️ Este é um **espelho declarado** de `framework/std/src/event/utils.ts`, onde `isFarEnough`
 * compara `dx² + dy² > 4` — ou seja, distância > 2. Ele não é importado de lá porque este
 * arquivo não importa nada (é o que o torna executável fora do build), e **há teste que lê o
 * `utils.ts` e reprova se os dois números divergirem** — pelo mesmo mecanismo que o gate do
 * `decidir-pan.ts` usa, em vez de digitar o número e torcer.
 */
export const FOLGA_DO_FRAMEWORK = 2;

/**
 * A folga, em pixels de tela, dentro da qual dois toques ainda são "no mesmo lugar".
 *
 * 10px é a convenção das duas plataformas que importam aqui (o *touch slop* do Android é 8dp; o
 * do iOS fica na casa dos 10pt) e é o mesmo número que `FOLGA_DO_DEDO` do `decidir-pan.ts`
 * adotou no card 1864291811674032036 — pela mesma razão, medida lá: **o centroide do contato se
 * desloca enquanto a pressão se acomoda**, e o dedo raramente pousa duas vezes no mesmo pixel.
 *
 * ⚠️ Ele é MAIOR que `FOLGA_DO_FRAMEWORK` de propósito. Se um dia ficar menor ou igual, o duplo
 * toque volta a ser descartado pelo tremor — que é metade do defeito de origem. Há teste
 * guardando essa relação.
 */
export const FOLGA_DO_TOQUE = 10;

/**
 * A janela entre os dois toques.
 *
 * 500ms é o valor que o próprio `ClickController` já usava para o mouse. Repeti-lo aqui em vez
 * de inventar um número do dedo é deliberado: **o que muda no toque é a identidade, não o
 * tempo**, e uma janela diferente faria o mesmo gesto responder diferente conforme a entrada.
 */
export const JANELA_DO_DUPLO_TOQUE_MS = 500;

/**
 * Quanto um contato pode se deslocar e ainda ser "o mesmo ponto".
 *
 * Usada nos DOIS lugares do `ClickController` que medem deslocamento — entre dois `pointerdown`
 * e entre o `pointerdown` e o `pointerup` do mesmo toque. Ter uma função só é o que impede os
 * dois de discordarem: no dia em que alguém mexer na folga do toque, os dois andam juntos.
 */
export function folgaDoContato(tipoDePonteiro: TipoDePonteiro | undefined): number {
  return tipoDePonteiro === 'touch' ? FOLGA_DO_TOQUE : FOLGA_DO_FRAMEWORK;
}

export interface ContatoRepetido {
  /** `pointerId` do `pointerdown` anterior. */
  idAnterior: number;
  /** `pointerId` deste `pointerdown`. */
  idAtual: number;
  /** `PointerEvent.pointerType` deste `pointerdown`. */
  tipoDePonteiro: TipoDePonteiro | undefined;
  /**
   * Milissegundos entre os dois `pointerdown`. Os dois carimbos saem do mesmo
   * `PointerEvent.timeStamp`, que é `DOMHighResTimeStamp` — mesma base do `performance.now()`,
   * sem depender de relógio de parede e sem drift entre as duas leituras.
   */
  msEntre: number;
  /** Distância, em pixels de tela, entre os dois pontos de contato. */
  deslocamento: number;
}

/**
 * A pergunta que o `ClickController` faz a cada `pointerdown`: *este contato continua a contagem
 * do anterior, ou começa uma nova?*
 *
 * Devolver `false` NÃO é um erro — é o caso comum (todo primeiro clique). O que ela decide é se
 * `_pointerDownCount` incrementa.
 *
 * As três condições ficam no mesmo lugar de propósito: tempo, proximidade e — só fora do toque —
 * identidade. Espalhá-las entre este arquivo e o chamador faria a próxima pessoa que mexer numa
 * delas não encontrar as outras duas.
 */
export function mesmoContatoRepetido({
  idAnterior,
  idAtual,
  tipoDePonteiro,
  msEntre,
  deslocamento,
}: ContatoRepetido): boolean {
  // A janela vale para TODAS as entradas — é o que separa "duplo" de "dois cliques seguidos".
  if (!(msEntre < JANELA_DO_DUPLO_TOQUE_MS)) return false;

  // A proximidade também vale para todas; o que muda é a folga. Para mouse e caneta ela é a do
  // framework, então este ramo reproduz exatamente o `!isFarEnough(...)` que estava aqui antes.
  if (deslocamento > folgaDoContato(tipoDePonteiro)) return false;

  // O dedo é o único que não tem identidade entre contatos: dois toques são dois `pointerId`.
  // Para ele, quem já respondeu "é o mesmo gesto?" foram o tempo e a proximidade, acima.
  if (tipoDePonteiro === 'touch') return true;

  // Mouse, caneta e ponteiro desconhecido seguem exatamente como antes.
  return idAnterior === idAtual;
}

/**
 * Este `pointerdown` deve suprimir os eventos de mouse de COMPATIBILIDADE?
 *
 * Card 1864882251313972786, medido no `dev` depois que a alça parou de engolir o 2º toque: o
 * duplo toque passou a abrir o editor de texto — e o editor fechava 36 ms depois:
 *
 *     +EDITOR @402 · focusin @415 · mousedown→rich-text @436 · -EDITOR @438
 *
 * Depois de um toque, o navegador EMULA `mousedown`/`mouseup` para páginas que só escutam mouse.
 * No mouse de verdade o `mousedown` vem ANTES do duplo clique ser contado; no toque ele chega
 * DEPOIS — com o editor já montado — e o fecha.
 *
 * A spec de Pointer Events dá a ferramenta exata: `preventDefault()` no `pointerdown` suprime os
 * eventos de mouse emulados daquele contato e NÃO suprime o `click`. É restrito ao toque que
 * completa o duplo toque — o 1º toque, o mouse e a caneta ficam exatamente como estão, porque
 * suprimir compatibilidade de todo toque quebraria qualquer widget que ainda escute `mousedown`.
 */
export function suprimirEventosDeCompatibilidade(
  tipoDePonteiro: TipoDePonteiro | undefined,
  contagem: number
): boolean {
  return tipoDePonteiro === 'touch' && contagem === 2;
}
